import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate, useRoutes } from 'react-router-dom';

// Import shared routes
import { publicRoutes } from './public';
import { systemRoutes } from './system';

// Import PointPay Public pages
const PublicHomePage = lazy(() => import('src/pointpay-public/pages/home'));
const CustomerPaymentConfirmation = lazy(
  () => import('src/pointpay-public/pages/customer-payment-confirmation')
);
const PaymentLinkPage = lazy(() => import('src/pointpay-public/pages/payment-link'));
const DonatePage = lazy(() => import('src/pointpay-public/pages/donate'));
const RegisterPage = lazy(() => import('src/pointpay-public/pages/register'));
const OnboardingPage = lazy(() => import('src/features/onboarding'));
const SecureUploadPage = lazy(() => import('src/pointpay-public/pages/secure-upload'));
const SecFileUploadPage = lazy(() => import('src/pointpay-public/pages/sec-file-upload'));
const BusinessInfoPage = lazy(() => import('src/pointpay-public/pages/business-info'));
const ReceiptPage = lazy(() => import('src/pointpay-public/pages/receipt'));
// Guarded: the guard refuses a reserved or non-generatable first segment before
// the page (and any Firestore/API read) is reached. See route-guard.tsx.
const ExperienceRoute = lazy(() => import('src/pointpay-public/pages/experience/route-guard'));

// Import 404 page
const JotformPage = lazy(() => import('src/pointpay-public/pages/integrations/jotform'));
const JotformWidget = lazy(
  () => import('src/pointpay-public/pages/integrations/jotform/pre-submit-widget')
);

const Page404 = lazy(() => import('src/pages/404'));
const InfoPage = lazy(() => import('src/pages/info'));

// ----------------------------------------------------------------------

/**
 * The pointpay-public route table.
 *
 * Exported so `__tests__/router-pointpay-public.test.tsx` can enumerate the real
 * top-level segments rather than a copy of them — the catch-all `/:publicId`
 * route below depends on that list staying in sync with
 * `RESERVED_PUBLIC_ID_SEGMENTS` in the shared contract.
 */
export function buildPointPayPublicRoutes(): RouteObject[] {
  return [
    // Home page at root
    {
      path: '/',
      element: <PublicHomePage />,
    },
    // Payment link page
    {
      path: '/payment-link/:id',
      element: <PaymentLinkPage />,
    },
    { path: '/confirm-payment', element: <CustomerPaymentConfirmation /> },
    // Donate page
    {
      path: '/donate/:shortCode',
      element: <DonatePage />,
    },
    // Business registration page
    {
      path: '/register',
      element: <RegisterPage />,
    },
    { path: '/register/onboarding', element: <OnboardingPage /> },
    // Secure upload (owner verification) page
    {
      path: '/secure-upload',
      element: <SecureUploadPage />,
    },
    // Secure file upload page (public document upload)
    {
      path: '/sec-file-upload',
      element: <SecFileUploadPage />,
    },
    // Business info public page
    {
      path: '/b/:businessName',
      element: <BusinessInfoPage />,
    },
    // Public receipt viewer (replaces the receipt page previously hosted in public-cloud-functions)
    {
      path: '/receipt/:id',
      element: <ReceiptPage />,
    },
    { path: '/integrations/jotform/:integrationId/pay', element: <JotformPage /> },
    { path: '/integrations/jotform/widget', element: <JotformWidget /> },
    { path: '/integrations/jotform/:integrationId/widget', element: <JotformWidget /> },
    // System routes (update, support, offline)
    ...systemRoutes,
    // Public routes (receipts, etc.)
    ...publicRoutes,
    // Site information and direct 404 route
    { path: '/info', element: <InfoPage /> },
    { path: '/404', element: <Page404 /> },

    // ------------------------------------------------------------------
    // Public Experiences live at the SITE ROOT: `{POINTPAY_PUBLIC}/{publicId}`
    // (owner decision, CONTRACTS.md §1.4a). Because `:publicId` is the first
    // path segment it shares this namespace with every static route above, so
    // these two entries MUST stay last — after every static route and after
    // `/404` — and BEFORE the `*` catch-all.
    //
    // Two things keep this safe as the router grows:
    //  - `ExperienceRouteGuard` only renders the page when the first segment
    //    matches `PUBLIC_ID_REGEX` (exactly 10 lowercase alphanumerics) and is
    //    not in `RESERVED_PUBLIC_ID_SEGMENTS`; anything else goes to `/404`,
    //    so a partial static path such as `/donate` keeps 404-ing.
    //  - `__tests__/router-pointpay-public.test.tsx` enumerates the real route
    //    table and asserts every top-level segment is reserved or
    //    ungeneratable, and that each existing path still resolves to its own
    //    page. Add a new top-level route => update the contract's reserved list
    //    (a contract change) and that test.
    // ------------------------------------------------------------------
    { path: '/:publicId', element: <ExperienceRoute /> },
    { path: '/:publicId/return', element: <ExperienceRoute isReturn /> },

    // No match 404
    { path: '*', element: <Navigate to="/404" replace /> },
  ];
}

export default function RouterPointPayPublic() {
  return useRoutes(buildPointPayPublicRoutes());
}
